import React from 'react';
import { useTranslation } from 'react-i18next';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import LanguageIcon from '@mui/icons-material/Language';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import CustomBackdrop from '../../../components/CustomBackdrop/CustomBackdrop';
import {
  useGetCustomerReferencesQuery,
  useMarkCustomerReferenceObsoleteMutation,
  useDeleteCustomerReferenceMutation,
  useEditCustomerReferenceMutation,
} from '../../../gql/generated/graphql';
import { getNodes } from '../../../gql/helpers';
import { handleVisitPage, openCorrectedLink } from '../../../helpers/helpers';

interface CustomerListProps {
  companyId: number;
}

const CustomerList: React.FC<CustomerListProps> = ({ companyId }) => {
  const { t } = useTranslation();

  const {
    data: customerReferences,
    loading: customerReferencesLoading,
    refetch: refetchCustomerReferences,
  } = useGetCustomerReferencesQuery({ variables: { companyId } });

  const [markCustomerReferenceObsolete, { loading: markCustomerReferenceObsoleteLoading }] =
    useMarkCustomerReferenceObsoleteMutation();
  const [deleteCustomerReference, { loading: deleteCustomerReferenceLoading }] = useDeleteCustomerReferenceMutation();
  const [editCustomerReference, { loading: editCustomerReferenceLoading }] = useEditCustomerReferenceMutation();

  const customerReferencesNodes = getNodes(customerReferences?.getCustomerReferences);

  const handleSeeFinding = (row: any) => {
    openCorrectedLink(row?.findingUrl);
  };

  const handleWrong = async (row: any) => {
    const result = await markCustomerReferenceObsolete({
      variables: { customerReferenceId: row?.dbId },
    });
    if (result?.data?.markCustomerReferenceObsolete?.success) {
      await refetchCustomerReferences();
    }
  };

  const handleDelete = async (row: any) => {
    const result = await deleteCustomerReference({
      variables: { customerReferenceId: row?.dbId },
    });
    if (result?.data?.deleteCustomerReference?.success) {
      await refetchCustomerReferences();
    }
  };

  const createUpdateVariables = (row: any, newShareOfRevenue: number) => {
    return {
      customerReferenceId: row.dbId,
      customerReferenceName: row.customerReferenceName || null,
      wonYear: row.wonYear || null,
      revenueYearOne: row.revenueYearOne || null,
      revenueYearTwo: row.revenueYearTwo || null,
      shareOfRevenue: newShareOfRevenue,
      arr: row.arr || null,
      users: row.users || null,
      active: row.active || null,
      lostYear: row.lostYear || null,
      lossReason: row.lossReason || null,
      nps: row.nps || null,
      isDdDatapoint: row.isDdDatapoint || null,
    };
  };

  const handleProcessRowUpdate = async (newRow: any, oldRow: any) => {
    if (newRow.shareOfRevenue !== oldRow.shareOfRevenue) {
      let revenueShareValue = parseFloat(newRow.shareOfRevenue);
      if (!isNaN(revenueShareValue)) {
        if (revenueShareValue > 1) {
          revenueShareValue = revenueShareValue / 100;
        }

        try {
          const variables = createUpdateVariables(oldRow, revenueShareValue);

          const result = await editCustomerReference({
            variables,
            update: (cache, { data }) => {
              if (data?.editCustomerReference?.success) {
                cache.modify({
                  fields: {
                    getCustomerReferences(existing) {
                      if (existing?.edges) {
                        const updatedEdges = existing.edges.map((edge: any) => {
                          if (edge?.node?.dbId === oldRow.dbId) {
                            return {
                              ...edge,
                              node: {
                                ...edge.node,
                                shareOfRevenue: revenueShareValue,
                              },
                            };
                          }
                          return edge;
                        });
                        return {
                          ...existing,
                          edges: updatedEdges,
                        };
                      }
                      return existing;
                    },
                  },
                });
              }
            },
          });

          if (result.data?.editCustomerReference?.success) {
            newRow.shareOfRevenue = revenueShareValue;
            return newRow;
          } else {
            alert(`Failed to update revenue share: ${result.data?.editCustomerReference?.message || 'Unknown error'}`);
            return oldRow;
          }
        } catch (error) {
          alert('Failed to update revenue share. Please try again.');
          return oldRow;
        }
      }
    }

    return newRow;
  };

  const columns: GridColDef[] = [
    {
      field: 'IAB1',
      headerName: 'Category',
      width: 150,
    },
    {
      field: 'shortName',
      headerName: 'Name',
      width: 150,
    },
    {
      field: 'crawlUrl',
      headerName: 'URL',
      width: 150,
    },
    {
      field: 'genericCustomer',
      headerName: 'Is Generic',
      width: 100,
      renderCell: params => {
        return params?.formattedValue ? (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
            <DoneIcon fontSize="small" />
          </Box>
        ) : null;
      },
    },
    {
      field: 'ISDd',
      headerName: 'is DD',
      width: 80,
      renderCell: params => {
        return params?.formattedValue ? (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
            <DoneIcon fontSize="small" />
          </Box>
        ) : null;
      },
    },
    {
      field: 'sicCode',
      headerName: 'SIC',
      width: 80,
    },
    {
      field: 'IAB2',
      headerName: 'Category 2',
      width: 100,
    },
    {
      field: 'IAB3',
      headerName: 'Sub',
      width: 100,
    },
    {
      field: 'shareOfRevenue',
      headerName: 'Revenue Share',
      width: 120,
      editable: true,
      renderCell: params => {
        const displayValue = params.row?.shareOfRevenue;

        if (displayValue === null || displayValue === undefined) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              N/A
            </Box>
          );
        }

        const percentValue = displayValue <= 1 ? displayValue * 100 : displayValue;
        return (
          <Box display="flex" alignItems="center" height="100%">
            {percentValue.toFixed(1)}%
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      editable: false,
      sortable: false,
      width: 200,
      renderCell: params => (
        <>
          <IconButton size="small" onClick={() => handleSeeFinding(params.row)}>
            <Tooltip title="See finding">
              <VisibilityIcon fontSize="small" />
            </Tooltip>
          </IconButton>
          <IconButton size="small" onClick={() => handleVisitPage(params.row?.crawlUrl)}>
            <Tooltip title="Visit customer page">
              <LanguageIcon fontSize="small" />
            </Tooltip>
          </IconButton>
          <IconButton size="small" onClick={() => handleWrong(params.row)}>
            <Tooltip title="Mark as wrong">
              <CancelPresentationIcon fontSize="small" />
            </Tooltip>
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row)}>
            <Tooltip title="Delete">
              <ClearIcon fontSize="small" />
            </Tooltip>
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box display="flex" flex={1} flexDirection="column">
      <Typography variant="h6" sx={{ mb: 2 }}>
        Customer References
      </Typography>
      <DataGrid
        loading={customerReferencesLoading}
        disableRowSelectionOnClick
        rows={customerReferencesNodes || []}
        columns={columns}
        rowHeight={35}
        getRowId={row => row?.dbId || Math.random()}
        processRowUpdate={handleProcessRowUpdate}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 80]}
        sx={{ height: 600 }}
      />
      <CustomBackdrop
        open={markCustomerReferenceObsoleteLoading || deleteCustomerReferenceLoading || editCustomerReferenceLoading}
      />
    </Box>
  );
};

export default CustomerList;
